import {Controller} from "@hotwired/stimulus";
import Cookies from "js-cookie";

declare global {
  interface Window {confetti: any}
}

export default class extends Controller {
  static values = {duration: Number};

  static targets = [
    "loading",
    "achieved",
    "final",
    "progressBar",
    "vimeo",
    "trigger"
  ];

  // values
  declare durationValue: number;

  // targets
  declare loadingTarget: HTMLElement;
  declare achievedTarget: HTMLElement;
  declare finalTarget: HTMLElement;
  declare progressBarTarget: HTMLElement;
  declare hasTriggerTarget: boolean;
  declare triggerTarget: HTMLElement;
  declare vimeoTarget: HTMLIFrameElement;
  declare hasVimeoTarget: boolean;

  // internals
  private timeoutId?: number;
  private intervalId?: number;
  private onClose!: () => void;
  private started = false;

  connect() {
    this.addConfettiScriptTag();

    const alreadySeen = !!Cookies.get("final_hcp_claim_unlocked_component_seen");

    // Opens automatically the first time
    if (!alreadySeen) {
      this.open();
      this.startOnce();
    }

    this.onClose = this.handleClose.bind(this);
    document.addEventListener("tlw-modal:close", this.onClose);
  }

  disconnect() {
    if (this.timeoutId) clearTimeout(this.timeoutId);
    if (this.intervalId) clearInterval(this.intervalId);
    document.removeEventListener("tlw-modal:close", this.onClose);
  }

  // data-action="click->admin--final-hcp-claim-unlocked#open"
  open(event?: Event) {
    event?.preventDefault(); // prevent any other behaviour

    document.dispatchEvent(
      new CustomEvent("tlw-modal:trigger-open", {
        detail: {modal: "final_hcp_claim_unlocked_modal"},
        bubbles: true
      })
    );

    // Start the timed sequence
    requestAnimationFrame(() => this.startOnce());
  }

  // click from screen 2 → screen 3
  toFinal() {
    this.show("final");
    this.startConfetti();
  }

  handleClose() {
    Cookies.set("final_hcp_claim_unlocked_component_seen", "true", {sameSite: "strict"});
    this.stopConfetti();
    this.resetModalContent();
    this.muteVimeo();
  }

  // "Replay" button: data-action="click->admin--final-hcp-claim-unlocked#replay"
  replay() {
    if (this.timeoutId) clearTimeout(this.timeoutId);
    this.stopConfetti();
    this.resetModalContent();
    this.show("loading");
    this.startOnce();
  }

  private startOnce() {
    if (this.started) return;
    this.started = true;

    const dur = this.durationValue;

    if (this.progressBarTarget) {
      this.progressBarTarget.style.transitionDuration = `${dur}ms`;
      this.progressBarTarget.style.width = "0%";
      // Force reflow so the width change to 100% triggers a transition
      void (this.progressBarTarget as any).offsetWidth;
      requestAnimationFrame(() => {
        this.progressBarTarget.style.width = "100%";
      });
    }

    this.show("loading");
    this.timeoutId = window.setTimeout(() => this.show("achieved"), dur);
  }

  private show(name: "loading" | "achieved" | "final") {
    this.stopConfetti();
    this.loadingTarget.classList.toggle("hidden", name !== "loading");
    this.achievedTarget.classList.toggle("hidden", name !== "achieved");
    this.finalTarget.classList.toggle("hidden", name !== "final");
    if (name === "achieved") this.animateAchieved();
  }

  private startConfetti() {
    if (window.confetti) {
      this.showConfetti();
      this.intervalId = window.setInterval(() => this.showConfetti(), 1500) as unknown as number;
    }
  }

  private showConfetti = () => {
    const rand = (min: number, max: number) => Math.random() * (max - min) + min;
    if (!window.confetti) return;
    window.confetti({
      angle: rand(45, 135),
      origin: {y: 0.6},
      particleCount: rand(50, 100),
      spread: rand(50, 70),
      zIndex: 1
    });
  };

  private stopConfetti = () => {
    if (this.intervalId) clearInterval(this.intervalId);
    this.intervalId = undefined;
  };

  private addConfettiScriptTag() {
    const src = "https://cdn.jsdelivr.net/npm/canvas-confetti@1.9.4/dist/confetti.browser.min.js";
    if (document.querySelector(`script[src="${src}"]`)) return; // avoid duplicates
    const script = document.createElement("script");
    script.src = src;
    script.crossOrigin = "anonymous";
    script.integrity = "sha512-YOBx/ev2NFNqeGwTFKab9vwK41aoUQO86+KN81hpiOwJbojDaKX05KJPnScTpMhXiMLA9Xf1zzXwjR8TaUnYKQ==";
    script.referrerPolicy = "no-referrer";
    document.head.appendChild(script);
  }

  private muteVimeo() {
    if (!this.hasVimeoTarget) return;
    this.vimeoTarget.contentWindow?.postMessage(
      JSON.stringify({method: "setVolume", value: 0}),
      "*"
    );
  }

  private animateAchieved() {
    const list = this.achievedTarget.querySelector<HTMLElement>("[data-achievement-list]");
    const items = Array.from(this.achievedTarget.querySelectorAll<HTMLElement>("[data-achievement-item]"));
    const gif = this.achievedTarget.querySelector<HTMLElement>("[data-achievement-item-gif]");
    const firstDuration = 600; // load first item in slightly snappier.
    const duration = 900;

    // Expand list
    requestAnimationFrame(() => {
      if (list) {
        list.classList.remove("max-h-0", "opacity-0");
        list.classList.add("max-h-full", "opacity-100");
      }
    });

    // Stagger items
    items.forEach((el, i) => {
      el.style.transitionDelay = i === 0 ? `${firstDuration}ms` : `${firstDuration + i * duration}ms`;
      requestAnimationFrame(() => {
        el.classList.remove("opacity-0", "translate-y-4");
        el.classList.add("opacity-100", "translate-y-0");
      });
    });

    // GIF last
    if (gif) {
      const delay = duration + items.length * duration;
      gif.style.transitionDelay = `${delay}ms`;
      requestAnimationFrame(() => {
        gif.classList.remove("max-h-0", "opacity-0", "translate-y-4");
        gif.classList.add("max-h-[400px]", "opacity-100", "translate-y-0");
      });
    }
  }

  private resetModalContent() {
    this.started = false;
    this.resetAchievementAnimations();
  }

  private resetAchievementAnimations() {
    const list = this.achievedTarget.querySelector<HTMLElement>("[data-achievement-list]");
    const items = Array.from(this.achievedTarget.querySelectorAll<HTMLElement>("[data-achievement-item]"));
    const gif = this.achievedTarget.querySelector<HTMLElement>("[data-achievement-item-gif]");

    // Collapse list
    if (list) {
      list.classList.add("max-h-0", "opacity-0");
      list.classList.remove("max-h-full", "opacity-100");
    }

    // Reset items
    items.forEach((el) => {
      el.style.transitionDelay = "0ms";
      el.classList.add("opacity-0", "translate-y-4");
      el.classList.remove("opacity-100", "translate-y-0");
      void (el as any).offsetWidth; // reflow so future animations retrigger
    });

    // Reset gif
    if (gif) {
      gif.style.transitionDelay = "0ms";
      gif.classList.add("max-h-0", "opacity-0", "translate-y-4");
      gif.classList.remove("max-h-[400px]", "opacity-100", "translate-y-0");
      void (gif as any).offsetWidth;
    }
  }
}
